/**
 * Styles do modulo Kanbans (Fase 6).
 *
 * Reusa primitives de pages/Orcamentos/styles + adiciona componentes
 * proprios para o board (colunas horizontais + cards arrastaveis).
 */
import styled, { css } from "styled-components";

import { getToneTokens } from "../../styles/shared";

export {
  Card,
  DangerButton,
  EmptyMsg,
  ErrorMsg,
  Field,
  FieldLabel,
  FieldRow,
  FieldValue,
  Form,
  FormFooter,
  IconButton,
  InfoMsg,
  InlineHint,
  Input,
  Pill,
  PrimaryButton,
  SearchInput,
  SecondaryButton,
  SectionTitle,
  Select,
  Textarea,
} from "../Orcamentos/styles";

// ============================================================
// Lista de kanbans (cards-style)
// ============================================================

export const BoardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
  margin-top: 12px;
`;

export const BoardCard = styled.div`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  cursor: pointer;
  transition: border-color 0.12s ease, transform 0.05s ease;

  &:hover {
    border-color: ${({ theme }) => theme.accent};
  }

  &:active {
    transform: scale(0.99);
  }
`;

export const BoardCardTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.textPrimary};
`;

export const BoardCardMeta = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.textSecondary};
`;

export const BoardCardDescricao = styled.p`
  margin: 0;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.textSecondary};
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

// ============================================================
// Board view (colunas horizontais + cards)
// ============================================================

export const BoardScroller = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 8px;
  /* Scrollbar discreta */
  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.border};
    border-radius: 4px;
  }
`;

export const BoardColumns = styled.div`
  display: flex;
  gap: 12px;
  min-height: 60vh;
  padding: 4px;
`;

export const ColumnRoot = styled.div`
  flex: 0 0 280px;
  background: ${({ theme }) => theme.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  max-height: 75vh;

  ${({ $dragOver }) =>
    $dragOver &&
    css`
      outline: 2px dashed ${({ theme }) => theme.accent};
      outline-offset: -2px;
    `}
`;

export const ColumnHeader = styled.div`
  padding: 10px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: ${({ theme, $tone }) => {
    if (!$tone || $tone === "neutral") return "transparent";
    const t = getToneTokens(theme, $tone);
    return t.background;
  }};
  color: ${({ theme, $tone }) => {
    if (!$tone || $tone === "neutral") return theme.textPrimary;
    const t = getToneTokens(theme, $tone);
    return t.color;
  }};
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;

  strong {
    font-size: 0.88rem;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const ColumnCount = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.1);
  color: inherit;
`;

export const ColumnBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const ColumnFooter = styled.div`
  padding: 6px 8px;
  border-top: 1px solid ${({ theme }) => theme.border};
  display: flex;
  gap: 4px;
`;

export const CartaoRoot = styled.div`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 8px;
  padding: 8px 10px;
  cursor: grab;
  display: flex;
  flex-direction: column;
  gap: 4px;
  transition: border-color 0.12s ease, transform 0.05s ease;

  &:hover {
    border-color: ${({ theme }) => theme.accent};
  }

  &:active {
    cursor: grabbing;
    transform: scale(0.99);
  }

  ${({ $dragging }) =>
    $dragging &&
    css`
      opacity: 0.4;
    `}
`;

export const CartaoTitulo = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

export const CartaoMeta = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  font-size: 0.7rem;
  color: ${({ theme }) => theme.textSecondary};
`;

// ============================================================
// Estagio editor (renomear, remover)
// ============================================================

export const StageEditorRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  padding: 6px 4px;
  border-bottom: 1px solid ${({ theme }) => theme.border};

  &:last-child {
    border-bottom: none;
  }
`;
